import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import type { ReviewAnswer } from "@/portals/patient/api/reviewItemsApi";

/**
 * Patient Conversation — the questions a clinician asked about a medication
 * review, the patient's replies and the final decision, as one chat-style
 * thread. The same component on both sides: the clinician's review screen
 * (viewer "clinician") and the patient's Human Review Queue / review modal
 * (viewer "patient"); only the wording of who's who changes.
 */

export type ConversationViewer = "clinician" | "patient";

export type ConversationMessage =
  | { kind: "question"; at: string; text: string }
  | { kind: "reply"; at: string; answer: ReviewAnswer; comment: string | null }
  | { kind: "decision"; at: string; label: string; reason?: string | null; details?: ReactNode };

/** The patient's answer choices as a short tag on their reply (REPLY = plain text, no tag). */
const ANSWER_TAGS: Record<ReviewAnswer, string | null> = {
  CURRENT_MEDICINE: "This is my current medicine",
  NO_LONGER_TAKE: "I no longer take this",
  DOSE_CHANGED: "My dose changed",
  TAKE_BOTH: "I take both",
  NOT_RECOGNIZED: "I don't recognize this",
  NOT_SURE: "I'm not sure",
  SKIPPED: null,
  REPLY: null,
  SAME_PERSON: "This is me",
  NOT_SAME_PERSON: "This isn't me",
  USE_THIS_VALUE: "Picked the value they think is right",
};

/** Questions, replies and decisions, oldest first. */
export function buildConversation(parts: {
  questions: { text: string; askedAt: string }[];
  responses: { answer: ReviewAnswer; comment: string | null; respondedAt: string }[];
  decisions?: { at: string; label: string; reason?: string | null; details?: ReactNode }[];
}): ConversationMessage[] {
  return [
    ...parts.questions.map((q): ConversationMessage => ({ kind: "question", at: q.askedAt, text: q.text })),
    ...parts.responses.map((r): ConversationMessage => ({ kind: "reply", at: r.respondedAt, answer: r.answer, comment: r.comment })),
    ...(parts.decisions ?? []).map((d): ConversationMessage => ({ kind: "decision", ...d })),
  ].sort((a, b) => a.at.localeCompare(b.at));
}

function formatWhen(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day} · ${time}`;
}

const SENDER = {
  clinician: { clinician: "You", patient: "Patient" },
  patient: { clinician: "Your clinician", patient: "You" },
} as const;

interface PatientConversationProps {
  messages: ConversationMessage[];
  viewer: ConversationViewer;
  /** Adds a closing "waiting for …" line while the other side owes a reply. */
  waitingFor?: "patient" | "clinician" | null;
  emptyText?: string;
  className?: string;
  /** Show at most this many messages; the rest scroll (opened at the latest). */
  maxVisible?: number;
}

const MESSAGE_GAP = 12; // gap-3 between messages

export function PatientConversation({ messages, viewer, waitingFor = null, emptyText, className, maxVisible }: PatientConversationProps) {
  const subtitle = viewer === "clinician" ? "Questions and replies between you and the patient" : "Questions and replies between you and your clinician";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  // Cap the thread at `maxVisible` messages: measure where the next one starts.
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (!box || !maxVisible) return;
    const measure = () => {
      const items = box.querySelectorAll<HTMLElement>("[data-testid='conversation-message']");
      setMaxHeight(items.length > maxVisible ? items[maxVisible].offsetTop - MESSAGE_GAP : undefined);
    };
    measure();
    box.scrollTop = box.scrollHeight;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [messages, maxVisible, waitingFor]);
  return (
    <section aria-label="Patient Conversation" className={cn("rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB]", className)}>
      <header className="flex items-center gap-2.5 border-b border-[#E5E7EB] px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0A2947]">
          <Icon icon="lucide:messages-square" className="h-4 w-4 text-white" />
        </span>
        <div className="min-w-0">
          <p className="!mb-0 text-sm font-extrabold text-[#0A2947]">Patient Conversation</p>
          <p className="!mb-0 text-xs text-[#6B7280]">{subtitle}</p>
        </div>
      </header>

      <div className="px-4 py-4">
        {messages.length === 0 && !waitingFor ? (
          <p className="!mb-0 text-center text-sm text-[#9CA3AF]">{emptyText ?? "No conversation yet."}</p>
        ) : (
          <div
            ref={scrollRef}
            className={cn("relative", maxHeight !== undefined && "overflow-y-auto pr-1")}
            style={maxHeight !== undefined ? { maxHeight } : undefined}
          >
          <ol className="!mb-0 grid gap-3 !pl-0">
            {messages.map((m, index) => (
              <li key={`${m.kind}-${m.at}-${index}`} className="list-none" data-testid="conversation-message">
                {m.kind === "decision" ? (
                  <DecisionLine message={m} />
                ) : (
                  <Bubble message={m} viewer={viewer} />
                )}
              </li>
            ))}
            {waitingFor && (
              <li className="list-none" data-testid="conversation-waiting">
                <p className="!mb-0 flex items-center justify-center gap-2 text-xs font-semibold text-[#946200]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#F9CA39]" />
                  {waitingFor === "patient"
                    ? viewer === "patient" ? "Your clinician is waiting for your reply" : "Waiting for the patient's reply…"
                    : viewer === "clinician" ? "Waiting for your decision" : "Waiting for your clinician…"}
                </p>
              </li>
            )}
          </ol>
          </div>
        )}
      </div>
    </section>
  );
}

function Bubble({ message, viewer }: { message: Extract<ConversationMessage, { kind: "question" | "reply" }>; viewer: ConversationViewer }) {
  const from = message.kind === "question" ? "clinician" : "patient";
  const mine = from === viewer;
  const skipped = message.kind === "reply" && message.answer === "SKIPPED";
  const tag = message.kind === "reply" ? ANSWER_TAGS[message.answer] : null;
  const text = message.kind === "question" ? message.text : message.comment;

  return (
    <div className={cn("flex flex-col", mine ? "items-end" : "items-start")}>
      <p className="!mb-1 flex items-center gap-1.5 px-1 text-[11px] text-[#6B7280]">
        <Icon icon={from === "clinician" ? "lucide:stethoscope" : "lucide:user-round"} className="h-3 w-3" />
        <span className="font-semibold text-[#374151]">{SENDER[viewer][from]}</span>
        <span>· {formatWhen(message.at)}</span>
      </p>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm",
          mine ? "rounded-tr-sm bg-[#0A2947] text-white" : "rounded-tl-sm border border-[#E5E7EB] bg-white text-[#0A2947]",
        )}
      >
        {message.kind === "question" && (
          <p className={cn("!mb-1 text-[10px] font-extrabold uppercase tracking-wide", mine ? "text-white/70" : "text-[#946200]")}>Question</p>
        )}
        {tag && (
          <span
            className={cn(
              "mb-1.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold",
              mine ? "bg-white/15 text-white" : "bg-[#FFF2F4] text-[#E4002B]",
            )}
          >
            {tag}
          </span>
        )}
        {skipped ? (
          <p className={cn("!mb-0 italic", mine ? "text-white/80" : "text-[#6B7280]")}>Skipped, left it to the clinician</p>
        ) : text ? (
          <p className="!mb-0 whitespace-pre-wrap break-words">{text}</p>
        ) : !tag ? (
          <p className={cn("!mb-0 italic", mine ? "text-white/80" : "text-[#6B7280]")}>No comment</p>
        ) : null}
      </div>
    </div>
  );
}

function DecisionLine({ message }: { message: Extract<ConversationMessage, { kind: "decision" }> }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex w-full items-center gap-3">
        <span className="h-px flex-1 bg-[#E5E7EB]" />
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#027A48]/25 bg-[#ECFDF3] px-3 py-1 text-xs font-bold text-[#027A48]">
          <Icon icon="lucide:badge-check" className="h-3.5 w-3.5" />
          Decision: {message.label}
        </span>
        <span className="h-px flex-1 bg-[#E5E7EB]" />
      </div>
      <p className="!mb-0 mt-1 text-[11px] text-[#9CA3AF]">{formatWhen(message.at)}</p>
      {message.reason && <p className="!mb-0 mt-1 max-w-[85%] text-xs text-[#4B5563]">"{message.reason}"</p>}
      {message.details}
    </div>
  );
}
